import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, shareReplay } from 'rxjs';
import { FieldId } from '../../../../gk-dynamic-list/services/dictionary/dictionary.model';
import { PortalId } from '../../../../utils/portal-id/portal-id.model';
import {
  DocumentSection,
  DocumentSectionApi,
  DocumentSectionTypeId,
} from './document-section.model';

@Injectable()
export class DocumentSectionService {
  private documentSectionsMap = new Map<
    string,
    Observable<DocumentSection[]>
  >();
  private proxyAttachmentsSelectedMap = new Map<
    PortalId,
    BehaviorSubject<boolean>
  >();

  constructor(private httpClient: HttpClient) {}

  clearCache(portalId: PortalId, requestMode?: FieldId): void {
    this.documentSectionsMap.delete(this.getCacheKey(portalId, requestMode));
    this.proxyAttachmentsSelectedMap.delete(portalId);
  }

  get(
    portalId: PortalId,
    requestMode?: FieldId,
  ): Observable<DocumentSection[]> {
    const cacheKey = this.getCacheKey(portalId, requestMode);
    let documentSections$ = this.documentSectionsMap.get(cacheKey);

    if (!documentSections$) {
      documentSections$ = this.httpClient
        .get<
          DocumentSectionApi[]
        >(this.getDocumentSectionUrl(portalId, requestMode))
        .pipe(
          map((documentSectionsApi) =>
            documentSectionsApi.map((documentSectionApi) =>
              DocumentSection.fromApiToApp(documentSectionApi),
            ),
          ),
          shareReplay(1),
        );
      this.documentSectionsMap.set(cacheKey, documentSections$);
    }

    return documentSections$;
  }

  getCacheKey(portalId: PortalId, requestMode?: FieldId): string {
    return `${portalId}${requestMode ? `-${requestMode}` : ''}`;
  }

  getDocumentSectionUrl(portalId: PortalId, requestMode?: FieldId): string {
    return requestMode
      ? `/api/eod/kdokrodz/portal/${portalId}/tryb/${requestMode}/pliki`
      : `/api/eod/kdokrodz/portal/${portalId}/pliki`;
  }

  getProxyAttachmentsSelected(portalId: PortalId): BehaviorSubject<boolean> {
    let proxyAttachmentsSelected$ =
      this.proxyAttachmentsSelectedMap.get(portalId);

    if (!proxyAttachmentsSelected$) {
      proxyAttachmentsSelected$ = new BehaviorSubject<boolean>(false);
      this.proxyAttachmentsSelectedMap.set(portalId, proxyAttachmentsSelected$);
    }

    return proxyAttachmentsSelected$;
  }

  isProxyAttachmentAvailableToPayOnline(
    portalId: PortalId,
  ): Observable<boolean> {
    return this.get(portalId).pipe(
      map((documentSections) =>
        documentSections.some(
          (documentSection) =>
            documentSection.id ===
              DocumentSectionTypeId.PowerOfAttorneyStampDuty &&
            documentSection.stampDuty?.isPaymentEnabled,
        ),
      ),
      shareReplay(1),
    );
  }

  downloadSampleDocument(
    sampleDocumentId: string | number,
  ): Observable<HttpResponse<Blob>> {
    const body = { Id: sampleDocumentId };
    return this.httpClient.post('/api/system/web/plik/download', body, {
      responseType: 'blob',
      observe: 'response',
    });
  }
}
